import { resolveLandingsCdn } from './config'
import { resolveCategoryLink } from './category'
import { dispatchConnectOpen, loadConnectIframeScript } from './connect-loader'
import { bootstrapAnalytics } from './analytics-bridge'
import { initLegacyCarousels } from './carousel-runtime'
import { initLegacyAccordions } from './accordion-runtime'
import { initLegacySearch } from './search-runtime'
import { applyHostCleanup } from './host-cleanup-runtime'
import { resolveReferral, withCurrentSearch, withReferral } from './referral'
import { BaseElement, registerCustomElement } from './base-element'

interface LandingManifest {
  html: string
  css?: string
  cssUrl?: string
  category?: string
  deps?: string[]
  hostCleanup?: { removeClasses?: string[] }
}

const PRESERVE_QUERY_SELECTOR = 'a[data-evaro-preserve-query]'

function applyCurrentSearch(root: ShadowRoot): void {
  for (const link of root.querySelectorAll<HTMLAnchorElement>(PRESERVE_QUERY_SELECTOR)) {
    const href = link.getAttribute('href')
    if (href) link.setAttribute('href', withCurrentSearch(href, window.location.search))
  }
}

export class EvaroLandingElement extends BaseElement {
  static get observedAttributes(): string[] {
    return ['vendor', 'template', 'cdn', 'api-base', 'env', 'category']
  }

  private shadow: ShadowRoot
  private state: 'idle' | 'loading' | 'ready' | 'error' = 'idle'
  private currentSignature = ''

  constructor() {
    super()
    this.shadow = this.attachShadow({ mode: 'open' })
  }

  connectedCallback(): void {
    this.shadow.addEventListener('click', this.delegatedClick)
    this.load()
  }

  disconnectedCallback(): void {
    this.shadow.removeEventListener('click', this.delegatedClick)
  }

  attributeChangedCallback(_name: string, oldVal: string | null, newVal: string | null): void {
    if (oldVal === newVal) return
    if (this.isConnected) this.load()
  }

  private delegatedClick = (event: Event) => {
    const path = event.composedPath()
    for (const node of path) {
      if (!(node instanceof Element)) continue
      const link = node.getAttribute && node.getAttribute('data-connect-id')
      if (link) {
        event.preventDefault()
        const referral = resolveReferral(node, window.location.search)
        dispatchConnectOpen(this, referral ? withReferral(link, referral) : link, this.getAttribute('env') ?? undefined)
        return
      }
      // In-shadow anchor scroll: the host page's hash-based scroll-to-id
      // doesn't see elements inside this Shadow DOM, so `<a href="#section">`
      // links in the rendered manifest do nothing by default. Intercept them
      // and resolve the target inside our shadow root.
      if (node.tagName === 'A') {
        const href = node.getAttribute('href')
        if (href && href.length > 1 && href.startsWith('#')) {
          const target = this.shadow.getElementById(href.slice(1))
          if (target) {
            event.preventDefault()
            target.scrollIntoView({ behavior: 'smooth', block: 'start' })
            return
          }
        }
      }
    }
  }

  private signature(): string {
    return [
      this.getAttribute('vendor') || '',
      this.getAttribute('template') || '',
      this.getAttribute('cdn') || '',
      this.getAttribute('api-base') || '',
      this.getAttribute('env') || '',
      this.getAttribute('category') || '',
    ].join('|')
  }

  async load(): Promise<void> {
    const vendor = this.getAttribute('vendor')
    const template = this.getAttribute('template')
    if (!vendor || !template) {
      this.renderError('Missing required attributes: vendor + template')
      return
    }

    const signature = this.signature()
    if (this.state === 'loading' && signature === this.currentSignature) return
    this.currentSignature = signature
    this.state = 'loading'

    const cdn = resolveLandingsCdn({ cdn: this.getAttribute('cdn'), env: this.getAttribute('env') })
    const category = this.getAttribute('category')
    const flatUrl = `${cdn}/v2/${vendor}/${template}/manifest.json`
    // A category-content template (content AND CTA vary by category) ships one
    // manifest per category at a nested path. Try that first when a category is
    // set; fall back to the flat manifest so CTA-only category templates
    // (callout) and category-less templates keep resolving unchanged.
    const nestedUrl = category
      ? `${cdn}/v2/${vendor}/${template}/${encodeURIComponent(category)}/manifest.json`
      : null

    try {
      // Resolve the category link alongside the manifest fetch so it never sits
      // on the critical path. A category-driven template marks each CTA with
      // `data-connect-category` and bakes no link of its own; this is the link.
      const linkPromise: Promise<string | null> = category
        ? resolveCategoryLink(cdn, vendor, category)
        : Promise.resolve(null)

      let manifestUrl = nestedUrl ?? flatUrl
      let res = await fetchWithRetry(manifestUrl)
      if (nestedUrl && !res.ok) {
        manifestUrl = flatUrl
        res = await fetchWithRetry(flatUrl)
      }

      // A category the vendor names but Evaro has not published — no per-category
      // manifest and no flat one — leaves an empty slot, never a broken page.
      if (category && !res.ok) {
        this.clearShadow()
        this.state = 'ready'
        return
      }
      if (!res.ok) throw new Error(`Manifest fetch failed: ${res.status} ${manifestUrl}`)
      const manifest = (await res.json()) as LandingManifest

      let cssText = manifest.css ?? ''
      if (!cssText && manifest.cssUrl) {
        // Resolve `./styles.css` (or any relative path) against the manifest URL,
        // not the host page URL. Without this the browser would fetch
        // `{hostPage}/styles.css` and 404 every time.
        const cssAbsolute = new URL(manifest.cssUrl, manifestUrl).toString()
        const cssRes = await fetch(cssAbsolute, { credentials: 'omit' })
        if (cssRes.ok) cssText = await cssRes.text()
      }

      // The `category` attribute selects which consultation the CTA opens. If the
      // vendor names a category Evaro has not published in categories.json, render
      // nothing and raise no error — an unprovisioned category leaves an empty
      // slot, never a broken page.
      const categoryLink = await linkPromise
      if (category && !categoryLink) {
        this.clearShadow()
        this.state = 'ready'
        return
      }

      // The manifest bakes absolute asset URLs (so they resolve when the
      // landing is embedded on a third-party site). Repoint them at the CDN the
      // manifest itself was just loaded from — loader, manifest and `/l/` assets
      // always co-deploy, so this origin is always reachable, whichever one was
      // baked in at build time.
      this.render(rewriteCdnHosts(manifest.html, cdn), rewriteCdnHosts(cssText, cdn))
      applyCurrentSearch(this.shadow)
      if (categoryLink) this.applyCategoryLink(categoryLink)
      // Wire JS-dependent legacy carousel patterns inside the shadow root. The
      // manifest HTML had its <script> tags stripped during render for security,
      // so any rotator/pager that needs JS gets initialised here from our trusted
      // runtime against well-known class-name conventions (.carousel-card,
      // .owl-carousel). No-op when the markup is absent.
      initLegacyCarousels(this.shadow)
      initLegacyAccordions(this.shadow)
      initLegacySearch(this.shadow, {
        vendorHash: vendor,
        env: this.getAttribute('env') ?? undefined,
        host: this,
      })
      // Apply any manifest-declared host-page cleanup (e.g. dropping Shopify's
      // `.reading-width .article-content` wrapper classes for Fetch's
      // /pages/health embed). One-shot mutation on the host document, no-op
      // when the manifest doesn't ship the field.
      applyHostCleanup(manifest.hostCleanup?.removeClasses)
      // Warm the Connect launcher in the background so the first interaction —
      // CTA click, search submit, anything — opens the overlay immediately
      // instead of waiting on the iframe.js download. Without this, the
      // perceived delay between the user's click and the overlay appearing
      // reads as "nothing happened" (search input clears, then a 0.5-2s gap
      // before Connect finally renders). Errors are swallowed: the prewarm is
      // best-effort and the real interaction will retry + surface its own
      // error if iframe.js is unreachable.
      loadConnectIframeScript({ env: this.getAttribute('env') ?? undefined }).catch(() => {})
      this.state = 'ready'

      bootstrapAnalytics({
        vendorHash: vendor,
        apiBase: this.getAttribute('api-base') ?? undefined,
        env: this.getAttribute('env') ?? undefined,
      })

      this.dispatchEvent(new CustomEvent('evaro:ready', { detail: { vendor, template }, bubbles: true }))
    } catch (err) {
      this.renderError(err instanceof Error ? err.message : String(err))
      this.state = 'error'
      this.dispatchEvent(new CustomEvent('evaro:error', { detail: { error: err }, bubbles: true }))
    }
  }

  private clearShadow(): void {
    while (this.shadow.firstChild) this.shadow.removeChild(this.shadow.firstChild)
  }

  /**
   * Wire the resolved category link onto the template's call-to-action(s). A
   * category-driven template marks each CTA with `data-connect-category` and
   * bakes no link of its own; this is where the real link arrives, so the
   * existing `data-connect-id` click delegation picks it up unchanged.
   */
  private applyCategoryLink(link: string): void {
    for (const cta of this.shadow.querySelectorAll('[data-connect-category]')) {
      cta.setAttribute('data-connect-id', link)
    }
  }

  /**
   * Extract @font-face declarations from CSS text so they can be injected
   * into the host document's <head>. CSS @font-face rules inside a Shadow DOM
   * <style> element do not reliably load font files across browsers; promoting
   * them to a <style> element in the light DOM ensures fonts are registered
   * and available for Shadow DOM to reference by font-family name.
   */
  private extractFontFaces(css: string): string[] {
    const blocks: string[] = []
    const regex = /@font-face\s*\{[^}]+\}/gi
    let match
    while ((match = regex.exec(css)) !== null) {
      blocks.push(match[0])
    }
    return blocks
  }

  /**
   * Inject <style> elements into the host document's <head> for each
   * @font-face block extracted from the CSS. Idempotent: tracks injected
   * styles by a key derived from the font source URL (or the block content
   * when no source URL is available) so repeated calls don't duplicate them.
   */
  private ensureFontFacesInHost(fontFaceBlocks: string[]): void {
    for (const block of fontFaceBlocks) {
      const srcMatch = block.match(/src:\s*url\(["']?([^"'\)]+)["']?\)/i)
      const fontSourceKey = srcMatch
        ? srcMatch[1].replace(/[^a-zA-Z0-9]/g, '-')
        : block.slice(0, 60).replace(/[^a-zA-Z0-9]/g, '-')
      const key = `evaro-fontface-${fontSourceKey}`
      if (document.getElementById(key)) continue
      const style = document.createElement('style')
      style.id = key
      style.appendChild(document.createTextNode(block))
      document.head.appendChild(style)
    }
  }

  // Content (html, css) originates from Evaro's prerendered CDN manifest under our own
  // build pipeline. We render via DOMParser + importNode rather than innerHTML to avoid
  // executing inline scripts and to keep the parsing path explicit. Host pages cannot
  // override the CDN to a hostile origin without controlling their own DOM anyway.
  private render(html: string, css: string): void {
    this.clearShadow()

    // Extract @font-face blocks and inject as <style> elements in the host
    // document so font files load reliably in the light DOM and are available
    // to the Shadow DOM by font-family name.
    const fontFaceBlocks = this.extractFontFaces(css)
    if (fontFaceBlocks.length > 0) {
      this.ensureFontFacesInHost(fontFaceBlocks)
    }

    if (css) {
      const styleEl = document.createElement('style')
      styleEl.appendChild(document.createTextNode(css))
      this.shadow.appendChild(styleEl)
    }

    const wrapper = document.createElement('div')
    wrapper.className = 'evaro-landing-root'

    const parsed = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html')
    const root = parsed.body.firstElementChild
    if (root) {
      for (const child of Array.from(root.childNodes)) {
        wrapper.appendChild(document.importNode(child, true))
      }
    }

    this.shadow.appendChild(wrapper)
  }

  private renderError(message: string): void {
    this.clearShadow()
    const errorEl = document.createElement('div')
    errorEl.className = 'evaro-landing-error'
    errorEl.setAttribute('role', 'alert')
    errorEl.style.display = 'none'
    errorEl.appendChild(document.createTextNode(message))
    this.shadow.appendChild(errorEl)
  }
}

/**
 * Single-retry fetch for the manifest. Retries once on network errors and 5xx
 * after a 500ms delay; 4xx fails fast (the resource simply doesn't exist).
 * Aimed at hiding brief CDN blips, not at masking real outages.
 */
async function fetchWithRetry(url: string): Promise<Response> {
  try {
    const res = await fetch(url, { credentials: 'omit' })
    if (res.ok || (res.status >= 400 && res.status < 500)) return res
  } catch {
    // Network error — fall through to retry.
  }
  await new Promise(resolve => setTimeout(resolve, 500))
  return fetch(url, { credentials: 'omit' })
}

/**
 * Repoint vendor-landings asset URLs at `cdn`, whichever origin the manifest
 * was loaded from. Manifests bake absolute asset URLs so they resolve when the
 * landing is embedded on a third-party site; this keeps them resolving to a
 * reachable origin regardless of which one was baked in at build time.
 *
 * Only the vendor-landings asset paths (`/l/`, `/v2/`, `/img/`) are touched, so
 * unrelated external links in the landing are left alone.
 */
export function rewriteCdnHosts(text: string, cdn: string): string {
  const origin = cdn.replace(/\/+$/, '')
  return text.replace(/https?:\/\/[^/"'\s)]+\/(l|v2|img)\//g, `${origin}/$1/`)
}

export function registerEvaroLanding(): void {
  registerCustomElement('evaro-landing', EvaroLandingElement)
}
